import { useState } from 'react';
import { useStore } from '../store.js';

const ENTITY_TYPES = ['Sole Proprietor', 'LLC', 'Corporation', 'Partnership', 'LLP', 'Ltd Partnership', 'Other'];
const STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC','PR'];

const REQUIRED = [
  ['business_name',            'Business name'],
  ['federal_tax_id',           'Federal Tax ID'],
  ['business_start_date',      'Business start date'],
  ['industry',                 'Industry'],
  ['entity_type',              'Entity type'],
  ['business_address_street',  'Street address'],
  ['business_city',            'City'],
  ['business_state',           'State'],
  ['business_zip',             'ZIP'],
];

function validateBusiness(data) {
  const errors = {};
  for (const [k, label] of REQUIRED) {
    if (!String(data[k] ?? '').trim()) errors[k] = `${label} is required.`;
  }
  if (!errors.federal_tax_id) {
    const ein = (data.federal_tax_id || '').replace(/\D/g, '');
    if (ein.length !== 9) errors.federal_tax_id = 'Must be 9 digits.';
  }
  if (!errors.business_zip) {
    const zip = (data.business_zip || '').replace(/\D/g, '').slice(0, 5);
    if (!/^\d{5}$/.test(zip)) errors.business_zip = 'Must be 5 digits.';
  }
  return errors;
}

function Field({ k, label, type = 'text', required, errors, clearError }) {
  const value = useStore((s) => s.data[k] ?? '');
  const setField = useStore((s) => s.setField);
  const error = errors?.[k];
  return (
    <div>
      <label htmlFor={`f-${k}`} className="field-label">
        {label}{required && <span className="text-red-500"> *</span>}
      </label>
      <input
        id={`f-${k}`}
        type={type}
        className={`field-input ${error ? 'is-error' : ''}`}
        aria-invalid={!!error}
        value={value}
        onChange={(e) => {
          setField(k, e.target.value);
          if (error) clearError?.(k);
        }}
      />
      {error && <p className="field-error">{error}</p>}
    </div>
  );
}

function Select({ k, label, options, required, errors, clearError }) {
  const value = useStore((s) => s.data[k] ?? '');
  const setField = useStore((s) => s.setField);
  const error = errors?.[k];
  return (
    <div>
      <label htmlFor={`f-${k}`} className="field-label">
        {label}{required && <span className="text-red-500"> *</span>}
      </label>
      <select
        id={`f-${k}`}
        className={`field-select ${error ? 'is-error' : ''}`}
        aria-invalid={!!error}
        value={value}
        onChange={(e) => {
          setField(k, e.target.value);
          if (error) clearError?.(k);
        }}
      >
        <option value="">— select —</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
      {error && <p className="field-error">{error}</p>}
    </div>
  );
}

export function BusinessInfo({ onNext }) {
  const data = useStore((s) => s.data);
  const [errors, setErrors] = useState({});
  const clearError = (k) => setErrors((e) => { const { [k]: _, ...rest } = e; return rest; });

  function handleContinue() {
    const errs = validateBusiness(data);
    setErrors(errs);
    if (Object.keys(errs).length === 0) onNext();
  }

  const props = { errors, clearError };

  return (
    <div className="space-y-6">
      <header>
        <h2 className="text-xl font-semibold text-brand-900">Business information</h2>
        <p className="text-sm text-slate-500">Tell us about your business.</p>
      </header>

      <div className="grid sm:grid-cols-2 gap-4">
        <Field k="business_name" label="Business name" required {...props} />
        <Field k="dba" label="Doing business as" {...props} />
        <Field k="federal_tax_id" label="Federal Tax ID (EIN)" required {...props} />
        <Field k="business_start_date" label="Business start date" type="date" required {...props} />
        <Field k="industry" label="Industry" required {...props} />
        <Select k="entity_type" label="Entity type" options={ENTITY_TYPES} required {...props} />
      </div>

      <div>
        <h3 className="font-medium text-slate-800 mb-2">Business address</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field k="business_address_street" label="Street" required {...props} />
          <Field k="business_address_line2" label="Suite / unit" {...props} />
          <Field k="business_city" label="City" required {...props} />
          <Select k="business_state" label="State" options={STATES} required {...props} />
          <Field k="business_zip" label="ZIP" required {...props} />
        </div>
      </div>

      <div>
        <h3 className="font-medium text-slate-800 mb-2">Landlord / mortgage</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field k="landlord_name" label="Landlord or mortgage holder name" {...props} />
          <Field k="landlord_amount" label="Monthly amount" {...props} />
        </div>
      </div>

      <div className="flex justify-end">
        <button className="btn-primary" onClick={handleContinue}>Continue</button>
      </div>
    </div>
  );
}
